import classNames from 'classnames';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { VideoDisplayMode } from '../../../features/meet/types';
import { ArrowGreaterThan } from '../../icons/arrow-greater-then';
import { CameraOffBackdrop, CameraOffLabel } from '../camera-off-ui';
import { RaisedHandCornerIndicator } from '../raised-hand-corner-indicator';

export type LocalVideoProps = {
  isVideoMuted: boolean;
  showHideButton: boolean;
  attachVideoStream: (videoRef: HTMLVideoElement) => void;
  clear?: (el?: HTMLVideoElement) => void;
  isHandRaised?: boolean;
  displayMode: VideoDisplayMode;
  /** Мобильный 1:1: вертикальное превью в правом верхнем углу поверх полноэкранного видео собеседника */
  mobileFullscreen?: boolean;
};

export const LocalVideo: React.FC<LocalVideoProps> = ({
  attachVideoStream,
  clear,
  isVideoMuted,
  showHideButton,
  isHandRaised = false,
  displayMode,
  mobileFullscreen = false,
}) => {
  const isCompact = displayMode === 'compact';
  const isEqual = displayMode === 'equal';
  const [isOpenVideoMe, setIsOpenVideoMe] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const borderRadiusClass =
    displayMode === 'mini' || displayMode === 'right-column' || displayMode === 'side-by-side'
      ? 'rounded-control'
      : 'rounded-card';

  // useLayoutEffect: привязка сразу после вставки в DOM (до отрисовки браузером).
  // В cleanup передаём КОНКРЕТНЫЙ элемент, чтобы detach затронул только этот <video>,
  // а не ВСЕ привязанные элементы — иначе при одновременном рендере двух VideoPanel
  // (режим инструмента + фоновый grid) размонтирование первого снимало бы трек и со второго.
  useLayoutEffect(() => {
    const el = videoRef.current;
    if (el) {
      attachVideoStream(el);
      return () => clear?.(el);
    }
    // Редкий кейс: ref ещё не установлен при смене раскладки — повторяем на следующем кадре
    let attachedEl: HTMLVideoElement | null = null;
    const raf = requestAnimationFrame(() => {
      attachedEl = videoRef.current;
      if (attachedEl) attachVideoStream(attachedEl);
    });
    return () => {
      cancelAnimationFrame(raf);
      clear?.(attachedEl ?? undefined);
    };
  }, [attachVideoStream, clear]);

  useEffect(() => {
    setIsOpenVideoMe(!isCompact);
  }, [isCompact]);

  const isMobilePip = mobileFullscreen && displayMode === 'default';

  const cameraOffLayer = (
    <>
      <CameraOffBackdrop
        visible={isVideoMuted}
        hasCapturedFrame={false}
        canvasRef={canvasRef}
        className={borderRadiusClass}
        objectFit="cover"
      />
      {(isOpenVideoMe || !isCompact) && (
        <CameraOffLabel visible={isVideoMuted} iconOnly={isMobilePip || displayMode === 'mini'} compact={isCompact}>
          Вы отключили камеру
        </CameraOffLabel>
      )}
    </>
  );

  if (displayMode === 'mini') {
    return (
      <div className="relative h-[96px] w-[72px] shrink-0 overflow-hidden rounded-control bg-ink">
        {isHandRaised && <RaisedHandCornerIndicator />}
        <video
          id="localvideo"
          ref={videoRef}
          className={classNames('bg-ink hide-video-controls h-full w-full object-cover rounded-control', {
            '!block': !isVideoMuted,
          })}
          style={{ visibility: isVideoMuted ? 'hidden' : 'visible' }}
          autoPlay
          muted
          playsInline
        />
        {cameraOffLayer}
        <div className="absolute bottom-1 left-1 z-[2] rounded-control bg-ink-muted/50 px-1.5 py-0.5 text-micro leading-3 text-white">
          Вы
        </div>
      </div>
    );
  }

  if (isMobilePip) {
    return (
      <div className="absolute right-3 top-[calc(12px+env(safe-area-inset-top,0px))] z-[21] aspect-[7/15] w-[27vw] max-w-[140px] overflow-hidden rounded-card shadow-overlay">
        {isHandRaised && <RaisedHandCornerIndicator />}
        <video
          id="localvideo"
          ref={videoRef}
          className={classNames('bg-ink hide-video-controls h-full w-full object-cover rounded-card', {
            '!block': !isVideoMuted,
          })}
          style={{ visibility: isVideoMuted ? 'hidden' : 'visible' }}
          autoPlay
          muted
          playsInline
        />
        {cameraOffLayer}
      </div>
    );
  }

  if (displayMode === 'right-column') {
    return (
      <div className={classNames('relative w-full h-full')}>
        {isHandRaised && <RaisedHandCornerIndicator />}
        <video
          id="localvideo"
          ref={videoRef}
          className={classNames('bg-ink hide-video-controls w-full h-full object-cover rounded-control', {
            '!block': !isVideoMuted,
          })}
          style={{ visibility: isVideoMuted ? 'hidden' : 'visible' }}
          autoPlay
          muted
          playsInline
        />
        {cameraOffLayer}
        <div
          className={classNames(
            'absolute top-[10px] w-auto bg-ink-muted/50 rounded-chip flex px-[10px] py-[5px] z-[2]',
            isHandRaised ? 'left-[52px]' : 'left-[15px]',
          )}
        >
          <div className="text-ui-xs text-white">Вы</div>
        </div>
      </div>
    );
  }

  if (displayMode === 'side-by-side') {
    return (
      <div className={classNames('relative w-full h-[120px] pt-[5px]')}>
        {isHandRaised && <RaisedHandCornerIndicator />}
        <video
          id="localvideo"
          ref={videoRef}
          className={classNames('bg-ink hide-video-controls w-full h-full object-cover rounded-control', {
            '!block': !isVideoMuted,
          })}
          style={{ visibility: isVideoMuted ? 'hidden' : 'visible' }}
          autoPlay
          muted
          playsInline
        />
        {cameraOffLayer}
        <div className="absolute bottom-[10px] left-[15px] w-auto bg-ink-muted/50 rounded-chip flex px-[10px] py-[5px] z-[2]">
          <div className="text-ui-xs text-white">Вы</div>
        </div>
      </div>
    );
  }

  if (isEqual && !isCompact) {
    return (
      <div className={classNames('relative w-full h-[50%] z-[21] pt-[5px]')}>
        {isHandRaised && <RaisedHandCornerIndicator />}
        <video
          id="localvideo"
          ref={videoRef}
          className={classNames('bg-ink hide-video-controls w-full h-full object-cover rounded-card', {
            '!block': !isVideoMuted,
          })}
          style={{ visibility: isVideoMuted ? 'hidden' : 'visible' }}
          autoPlay
          muted
          playsInline
        />
        {cameraOffLayer}
      </div>
    );
  }

  return (
    <div
      className={classNames(
        'w-[160px] h-[100px] lg:w-[280px] lg:h-[180px] max-w-[280px] max-h-[120px] lg:max-h-[180px] z-[21]',
        {
          relative: showHideButton,
          '!w-[100px] bg-ink-muted p-[10px] rounded-card !right-0 !top-[0] !max-h-[42px]': !isOpenVideoMe,
          'absolute top-[15px] right-[15px] h-full max-h-[180px]': !showHideButton,
        },
      )}
      style={isCompact && isEqual ? { zIndex: 21 } : undefined}
    >
      {isHandRaised && <RaisedHandCornerIndicator />}
      <video
        id="localvideo"
        ref={videoRef}
        className={classNames(
          'absolute top-0 left-0 bg-ink hide-video-controls w-full h-full object-cover rounded-card',
          {
            '!block': !isVideoMuted,
            '!opacity-0 !h-0': !isOpenVideoMe && isCompact,
          },
        )}
        style={{
          visibility: isVideoMuted ? 'hidden' : 'visible',
        }}
        autoPlay
        muted
        playsInline
      />
      <div
        className={classNames('absolute inset-0', {
          '!opacity-0 !h-0 pointer-events-none': !isOpenVideoMe && isCompact,
        })}
      >
        {cameraOffLayer}
      </div>
      {(!isOpenVideoMe || (isOpenVideoMe && !isVideoMuted)) && (
        <div
          className={classNames('absolute  w-auto bg-ink-muted/50 rounded-chip flex z-[1000] px-[10px] py-[5px]', {
            'w-max relative': !isOpenVideoMe,
            'bottom-[10px] left-[15px]': isOpenVideoMe,
          })}
        >
          <div className="text-ui-xs text-white">Вы</div>
        </div>
      )}
      {showHideButton && (
        <div
          className={classNames('absolute top-[9px] right-[15px] cursor-pointer duration-300 z-[1000]', {
            'rotate-[180deg]': !isOpenVideoMe,
          })}
          onClick={() => setIsOpenVideoMe(!isOpenVideoMe)}
        >
          <ArrowGreaterThan />
        </div>
      )}
    </div>
  );
};
